import { getSettings, updateSettings, getCompany, updateCompany, getCommissions, updateCommissions } from '../admin-api';
import { esc, formField } from '../admin-ui';
import { cmsErrorText } from './admin-cms';

/**
 * Settings (reworked 2026-10-14). "General Settings" used to be every row of
 * `game_settings` as a raw text box — meta tags, legacy `company.*` keys, the
 * payment-icon JSON — each also edited on its own tab, so the same value had
 * two editors and the last save won. The landing footer's own fields
 * (`footer.*`) were somewhere in that list with no hint of what they drive.
 *
 * Now each value has one home: Footer and Company say what they show on the
 * site, and Advanced keeps only the keys no other tab edits.
 */

/** Edited on another tab (or derived) — never shown here. */
const OWNED_ELSEWHERE = [/^meta\./, /^company\./, /^commission\./, /^payments\./, /^payment_methods$/, /^tallow\./];

export const FOOTER_FIELDS: Array<{ key: string; label: string; type: 'text' | 'textarea'; hint: string }> = [
  { key: 'footer.brand_name', label: 'Brand name', type: 'text', hint: 'Shown as "<brand> is owned and operated by …" in the landing footer.' },
  { key: 'footer.disclaimer', label: 'Disclaimer', type: 'textarea', hint: 'The last line of the landing footer.' },
];

export const FEATURE_KEYS = ['feature.kyc', 'feature.withdrawals', 'feature.invoices'];

/** Company Info fields, in the order a person fills them in. */
export const COMPANY_FIELDS: Array<{ key: string; label: string; footer?: boolean }> = [
  { key: 'name', label: 'Legal name', footer: true },
  { key: 'registrationNumber', label: 'Registration number', footer: true },
  { key: 'vatNumber', label: 'VAT number', footer: true },
  { key: 'addressLine1', label: 'Address', footer: true },
  { key: 'postalCode', label: 'Postal code', footer: true },
  { key: 'city', label: 'City', footer: true },
  { key: 'country', label: 'Country', footer: true },
  { key: 'jurisdiction', label: 'Jurisdiction' },
  { key: 'email', label: 'Contact e-mail' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website' },
  { key: 'domain', label: 'Domain' },
  { key: 'game_name', label: 'Game name' },
  { key: 'vat_rate', label: 'VAT rate %' },
  { key: 'bankName', label: 'Bank name' },
  { key: 'bankAccount', label: 'Bank account' },
  { key: 'bankSwift', label: 'Bank SWIFT' },
];

export function isAdvancedKey(key: string): boolean {
  if (FOOTER_FIELDS.some((f) => f.key === key) || FEATURE_KEYS.includes(key)) return false;
  return !OWNED_ELSEWHERE.some((re) => re.test(key));
}

function hint(text: string): string {
  return `<p class="text-[11px] text-muted -mt-1 mb-3">${esc(text)}</p>`;
}

function saveRow(id: string): string {
  return `
      <button class="adm-panel-save btn btn-gold min-h-11 px-4 text-sm mt-2" data-panel="${esc(id)}">Save</button>
      <span class="adm-panel-msg text-[13px] text-muted ml-3" data-panel-msg="${esc(id)}"></span>`;
}

function featureField(key: string, value: string): string {
  const id = `feat-${key}`;
  const on = value === 'true';
  return `
    <label class="flex items-center justify-between gap-3 mb-2 text-[13px] text-text" for="${esc(id)}">
      <span>${esc(key.replace(/^feature\./, ''))}</span>
      <select id="${esc(id)}" class="bg-surface-2 border border-border rounded text-text text-[13px] min-h-11 px-2.5">
        <option value="true"${on ? ' selected' : ''}>On</option>
        <option value="false"${on ? '' : ' selected'}>Off</option>
      </select>
    </label>`;
}

export async function mountSettings(container: HTMLElement): Promise<() => void> {
  let settings: Record<string, string> = {};
  let company: Record<string, unknown> = {};
  let commissions: Record<string, unknown> = {};

  try {
    const [s, c, m] = await Promise.all([getSettings(), getCompany(), getCommissions()]);
    settings = Object.fromEntries(Object.entries(s).map(([k, v]) => [k, String(v ?? '')]));
    company = c;
    commissions = m;
  } catch {
    container.innerHTML = '<p class="text-red-400 p-4">Failed to load settings.</p>';
    return () => {};
  }

  const companyKeys = [
    ...COMPANY_FIELDS.map((f) => f.key).filter((k) => k in company),
    // Anything core adds later still gets a box.
    ...Object.keys(company).filter((k) => !COMPANY_FIELDS.some((f) => f.key === k)),
  ];
  const labelOf = (k: string) => COMPANY_FIELDS.find((f) => f.key === k)?.label ?? k;
  const onFooter = (k: string) => COMPANY_FIELDS.find((f) => f.key === k)?.footer === true;
  const advancedKeys = Object.keys(settings).filter(isAdvancedKey).sort();

  container.innerHTML = `
    <h2 class="font-heading text-gold text-lg mb-4">Settings</h2>

    <div class="frame-pewter p-4 mb-4">
      <h3 class="font-heading text-gold text-sm mb-1">Landing footer</h3>
      ${hint('Company details below, CMS → Legal and Social, and Payments → Footer Payment Icons fill the rest of the footer.')}
      ${FOOTER_FIELDS.map((f) => formField(f.label, `footer-${f.key}`, f.type, settings[f.key] ?? '', { rows: 3 }) + hint(f.hint)).join('')}
      ${saveRow('footer')}
    </div>

    <div class="frame-pewter p-4 mb-4">
      <h3 class="font-heading text-gold text-sm mb-1">Company Info</h3>
      ${hint('Fields marked ● are printed in the landing footer; all of them go on invoices. Leave a field empty to hide it.')}
      <div class="grid gap-x-3 sm:grid-cols-2">
        ${companyKeys.map((k) => formField(`${labelOf(k)}${onFooter(k) ? ' ●' : ''}`, `company-${k}`, 'text', String(company[k] ?? ''))).join('')}
      </div>
      ${saveRow('company')}
    </div>

    <div class="frame-pewter p-4 mb-4">
      <h3 class="font-heading text-gold text-sm mb-3">Features</h3>
      ${FEATURE_KEYS.map((k) => featureField(k, settings[k] ?? 'false')).join('')}
      ${saveRow('features')}
    </div>

    <div class="frame-pewter p-4 mb-4">
      <h3 class="font-heading text-gold text-sm mb-3">Commissions</h3>
      ${Object.entries(commissions).map(([k, v]) => formField(k, `comm-${k}`, 'text', String(v ?? ''))).join('')}
      ${saveRow('comm')}
    </div>

    ${advancedKeys.length ? `
    <details class="frame-pewter p-4 mb-4">
      <summary class="font-heading text-gold text-sm cursor-pointer min-h-11 flex items-center">Advanced (${advancedKeys.length})</summary>
      ${hint('Raw values with no editor of their own. Meta tags, company, payments and commissions are edited on their own tabs.')}
      ${advancedKeys.map((k) => formField(k, `adv-${k}`, 'text', settings[k])).join('')}
      ${saveRow('adv')}
    </details>` : ''}
  `;

  /**
   * Keys like `commission.deposit_bps` contain dots, and an unescaped
   * `#gen-commission.deposit_bps` selector reads the dot as a class — it found
   * nothing, so every edit was silently replaced by the value already stored.
   */
  function read(id: string, fallback: unknown): string {
    const el = container.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#${CSS.escape(id)}`);
    return el ? el.value : String(fallback ?? '');
  }

  function changedSettings(prefix: string, keys: string[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const k of keys) {
      // A missing flag renders as Off, so Off is not a change.
      const shown = settings[k] ?? (prefix === 'feat' ? 'false' : '');
      const v = read(`${prefix}-${k}`, shown);
      if (v !== shown) out[k] = v;
    }
    return out;
  }

  container.querySelectorAll<HTMLButtonElement>('.adm-panel-save').forEach(btn => {
    btn.addEventListener('click', async () => {
      const panel = btn.dataset.panel!;
      const msg = container.querySelector<HTMLElement>(`[data-panel-msg="${panel}"]`);
      btn.disabled = true;
      try {
        if (panel === 'footer' || panel === 'features' || panel === 'adv') {
          const keys = panel === 'footer' ? FOOTER_FIELDS.map((f) => f.key) : panel === 'features' ? FEATURE_KEYS : advancedKeys;
          const prefix = panel === 'footer' ? 'footer' : panel === 'features' ? 'feat' : 'adv';
          const next = changedSettings(prefix, keys);
          await updateSettings(next);
          settings = { ...settings, ...next };
        } else if (panel === 'company') {
          const next = Object.fromEntries(companyKeys.map((k) => [k, read(`company-${k}`, company[k])]));
          await updateCompany(next);
          company = { ...company, ...next };
        } else if (panel === 'comm') {
          const next = Object.fromEntries(Object.keys(commissions).map((k) => [k, read(`comm-${k}`, commissions[k])]));
          await updateCommissions(next);
          commissions = { ...commissions, ...next };
        }
        if (msg) { msg.textContent = 'Saved.'; msg.classList.remove('text-red-400'); }
      } catch (err) {
        if (msg) { msg.textContent = `Save failed: ${cmsErrorText(err)}`; msg.classList.add('text-red-400'); }
      } finally {
        btn.disabled = false;
      }
    });
  });

  return () => {};
}
